import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import * as usersApi from '../api/users';
import * as badgesApi from '../api/badges';
import * as registrationApi from '../api/registration';
import { ErrorAlert, Field, SelectField, StatusPill } from '../components/ui';
import { useToast } from '../components/toast';
import { emailVerificationState } from '../lib/emailVerification';
import MfaSettings from '../components/MfaSettings';
import { ui, cn } from '../lib/ui';
import RibbonRack from '../components/ribbons/RibbonRack';

export default function Profile() {
  // `patchUser` (lib/auth.jsx) is the "clean setter" for the cached session
  // copy — it writes localStorage (via lib/session.js's writeSession) AND
  // updates the live auth state in one call, the same helper MfaSettings
  // uses after enrollment. lib/session.js itself only exports the raw
  // read/write/clear trio with no per-field patch, so patchUser is the right
  // seam rather than reaching into session.js directly.
  const { user, isAccredited, patchUser } = useAuth();

  // GET /users/{userId} on mount — the fresh record, not the copy cached at
  // login. Falls back to the session copy on error rather than blanking the
  // page; `userFetchError` drives a muted note instead of an alert since the
  // page is still fully usable off the fallback.
  const [freshUser, setFreshUser] = useState(null);
  const [userFetchError, setUserFetchError] = useState(null);
  const seededRef = useRef(false);

  const displayUser = freshUser || user;
  // Tested in lib/emailVerification.test.js — a conditional that renders
  // nothing passes lint and build while being wrong, which is how the missing
  // pill survived.
  const emailState = emailVerificationState(displayUser);

  const [firstName, setFirstName] = useState((user && user.firstName) || '');
  const [lastName, setLastName] = useState((user && user.lastName) || '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState(null);

  // Resending is a public endpoint (it must work for someone who cannot sign
  // in yet), so this needs no capability of its own — just the address.
  const [resendBusy, setResendBusy] = useState(false);
  const toast = useToast();

  const [earnedBadges, setEarnedBadges] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [badgesError, setBadgesError] = useState(false);

  const [whoCanInviteMe, setWhoCanInviteMe] = useState('all_users');
  const [whoCanMessageMe, setWhoCanMessageMe] = useState('all_users');
  const [privacySaved, setPrivacySaved] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [privacyError, setPrivacyError] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    usersApi
      .getUser(user.userId)
      .then((d) => {
        if (cancelled) return;
        setFreshUser(d.user);
        // Seed the edit form from the fresh record, but only the first time
        // it arrives — a slow response landing after the member has already
        // started typing must not clobber their in-progress edit.
        if (!seededRef.current) {
          seededRef.current = true;
          setFirstName(d.user.firstName || '');
          setLastName(d.user.lastName || '');
          if (d.user.privacySettings) {
            setWhoCanInviteMe(d.user.privacySettings.whoCanInviteMe || 'all_users');
            setWhoCanMessageMe(d.user.privacySettings.whoCanMessageMe || 'all_users');
          }
        }
      })
      .catch((err) => !cancelled && setUserFetchError(err));
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    Promise.all([badgesApi.listMyBadges(), badgesApi.listCatalog()])
      .then(([mine, all]) => {
        if (cancelled) return;
        setEarnedBadges(mine.badges || []);
        setCatalog(all.badges || []);
      })
      .catch(() => {
        if (cancelled) return;
        setBadgesError(true);
        setEarnedBadges([]);
        setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!displayUser) return null;

  async function onSaveProfile(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await usersApi.updateUser(displayUser.userId, { firstName, lastName });
      setSaved(true);
      setFreshUser((current) => (current ? { ...current, firstName, lastName } : current));
      patchUser({ firstName, lastName });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onResendVerification() {
    setResendBusy(true);
    try {
      await registrationApi.resendVerification(emailState.email);
      // The endpoint answers { requested: true } whether or not it sent, so a
      // caller cannot use it to probe which addresses exist. Say what was asked
      // for rather than claiming delivery this response cannot confirm.
      toast.success('Verification email requested — check your inbox.');
    } catch (err) {
      toast.error(err);
    } finally {
      setResendBusy(false);
    }
  }

  async function onChangePassword(event) {
    event.preventDefault();
    setPwError(null);
    setPwSaved(false);
    try {
      await usersApi.changePassword(displayUser.userId, { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setPwSaved(true);
    } catch (err) {
      setPwError(err);
    }
  }

  async function onSavePrivacy(event) {
    event.preventDefault();
    setPrivacyBusy(true);
    setPrivacyError(null);
    setPrivacySaved(false);
    try {
      await usersApi.updatePrivacySettings({ whoCanInviteMe, whoCanMessageMe });
      setPrivacySaved(true);
    } catch (err) {
      setPrivacyError(err);
    } finally {
      setPrivacyBusy(false);
    }
  }

  const earnedBadgeIds = new Set((earnedBadges || []).map((b) => b.badgeId));
  const unearnedCatalog = (catalog || []).filter((def) => !earnedBadgeIds.has(def.badgeId));

  return (
    <>
      <h1 className={ui.h1}>Your profile</h1>
      <div className={cn(ui.grid, ui.gridMd2)}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Account</h3>
          {userFetchError && (
            <p className={cn(ui.muted, 'text-body-s')}>
              Couldn&rsquo;t refresh your profile — showing your last signed-in details.
            </p>
          )}
          <p>
            <span className={cn(ui.muted, 'text-body-s')}>Email</span>
            <br />
            {/*
              The pill renders in BOTH states. It used to render only when
              verified, so an unverified account showed no status at all and no
              way to act on it — the one state where saying something matters.
              The Role line below has always shown a pill either way.
            */}
            {displayUser.email} <StatusPill status={emailState.pill} />
          </p>
          {emailState.canResend && (
            <div className={ui.alertInfo}>
              <p>
                Your email address isn&rsquo;t verified yet. Use the link in your
                verification email, or send yourself a new one.
              </p>
              <button
                className={ui.btnSecondary}
                type="button"
                disabled={resendBusy}
                onClick={onResendVerification}
              >
                {resendBusy ? 'Sending…' : 'Resend verification email'}
              </button>
            </div>
          )}
          <p>
            <span className={cn(ui.muted, 'text-body-s')}>Role</span>
            <br />
            <StatusPill status={isAccredited ? 'verified' : 'pending'} />{' '}
            {String(displayUser.role || '').replace(/-/g, ' ')}
          </p>
          {saved && <div className={ui.alertSuccess}>Profile saved.</div>}
          <ErrorAlert error={error} />
          <form onSubmit={onSaveProfile}>
            <Field
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Field
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <button className={ui.btnPrimary} type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Change password</h3>
          {pwSaved && <div className={ui.alertSuccess}>Password changed.</div>}
          <ErrorAlert error={pwError} />
          <form onSubmit={onChangePassword}>
            <Field
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <Field
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              hint="At least 12 characters with uppercase, lowercase, number, and symbol."
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button className={ui.btnPrimary} type="submit">
              Change password
            </button>
          </form>
        </section>
      </div>

      <div className={cn(ui.grid, ui.gridMd2, 'mt-4')}>
        <MfaSettings />

        <section className={ui.card}>
          <h3 className={ui.h3}>Privacy settings</h3>
          {privacySaved && <div className={ui.alertSuccess}>Privacy settings saved.</div>}
          <ErrorAlert error={privacyError} />
          <form onSubmit={onSavePrivacy}>
            <SelectField
              label="Who can invite me to events"
              hint="Controls which members can add you to their events."
              value={whoCanInviteMe}
              onChange={(e) => setWhoCanInviteMe(e.target.value)}
            >
              <option value="public">Anyone (public)</option>
              <option value="all_users">All signed-in members</option>
              <option value="group_members">My group members only</option>
              <option value="none">Nobody</option>
            </SelectField>
            <SelectField
              label="Who can message me"
              hint="Controls which members can send you direct messages."
              value={whoCanMessageMe}
              onChange={(e) => setWhoCanMessageMe(e.target.value)}
            >
              <option value="public">Anyone (public)</option>
              <option value="all_users">All signed-in members</option>
              <option value="group_members">My group members only</option>
              <option value="none">Nobody</option>
            </SelectField>
            <button className={ui.btnPrimary} type="submit" disabled={privacyBusy}>
              {privacyBusy ? 'Saving…' : 'Save privacy settings'}
            </button>
          </form>
        </section>
      </div>

      <div className={cn(ui.grid, ui.gridMd2, 'mt-4')}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Badges</h3>
          {earnedBadges === null ? (
            <p className={ui.muted}>Loading…</p>
          ) : (
            <>
              {earnedBadges.length ? (
                <div className={ui.row}>
                  {earnedBadges.map((b) => (
                    <span key={b.badgeId || b.awardId} className={ui.pillNeutral}>
                      {(b.definition && b.definition.name) || b.badgeId}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={ui.muted}>
                  No badges yet — join events and the community to earn them.
                </p>
              )}

              {badgesError ? (
                <p className={cn(ui.muted, 'text-body-s mt-4')}>Couldn&rsquo;t load the badge catalog.</p>
              ) : unearnedCatalog.length ? (
                <>
                  <p className={cn(ui.muted, 'text-body-s mt-4')}>Badges you can earn</p>
                  <ul className="text-body-s">
                    {unearnedCatalog.map((def) => (
                      <li key={def.badgeId} className={ui.muted}>
                        <strong>{def.name}</strong> — {def.description}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </section>
        <RibbonRack userId="me" />
      </div>
    </>
  );
}
